// Decklist text <-> editable rows. The editor's source of truth is the decklist
// TEXT (re-analysis takes a string), so we parse to rows for editing and
// serialize back to a Moxfield-style "N Name" list the API's parse_decklist
// accepts. Handles "N Name" / "Nx Name", trailing set codes ("(C21) 123",
// "[C21]"), *F*/*CMDR* markers, and // or # section/comment lines.
import { tc } from '../i18n'
import { sameCard } from './nameKeys'

export interface EditableCard {
  name: string
  quantity: number
}

/* eslint-disable i18next/no-literal-string -- decklist section headers, markers and section codes are parse grammar, never shown */
// --- Section grammar ---------------------------------------------------------
//
// ONE classifier for every helper in this file (parseDecklist,
// extractNonDeckSections, extractCommanders, removeCards), so a header can never
// be read two ways. It mirrors the parser /decks/analyze-complete actually runs:
// mtg-api app/decks.py parse_decklist_detailed, reached through
// app/services/deck_helpers.py parse_decklist_or_error, which then drops the
// sideboard / maybeboard / tokens entries. (app/decks.py also has a second,
// older parse_decklist used by other endpoints; where the two disagree, this
// follows parse_decklist_detailed. See the per-branch notes below.)
//
// The point of mirroring it: the rows the editor shows must be exactly the
// cards the analysis counts, and everything else must ride through a save
// untouched. A header the web read as "deck" but the API as "sideboard" turned
// sideboard cards into editable deck rows; the reverse hid deck cards.

/** The section a line switches to. sideboard / maybeboard / tokens are NOT the deck. */
export type DeckSection = 'main' | 'commander' | 'companion' | 'sideboard' | 'maybeboard' | 'tokens'

const NON_DECK: ReadonlySet<DeckSection> = new Set<DeckSection>(['sideboard', 'maybeboard', 'tokens'])

/** True for a section whose entries are not deck cards. */
export function isNonDeckSection(section: DeckSection): boolean {
  return NON_DECK.has(section)
}

// The API's exact bare-header list (parse_decklist_detailed). "about" is a DECK
// header there (it falls through to mainboard); the older parse_decklist skips
// it instead - analyze-complete does not use that one.
const BARE_HEADERS = new Set([
  'commander', 'commanders', 'mainboard', 'sideboard', 'maybeboard', 'deck',
  'creatures', 'instants', 'sorceries', 'artifacts', 'enchantments',
  'lands', 'planeswalkers', 'companion', 'about',
  'tokens', 'token', 'stickers', 'attractions', 'considering', 'maybe',
])
// Deliberately NO web-only extras (a bare "Main" used to be one). Any header
// the API does not know is a CARD line to it, which does not switch sections:
// "Sideboard / 1 Pyroblast / Main / 1 Sol Ring" keeps Sol Ring in the
// sideboard for the analysis, so it must stay hidden and carried through here.

// "[SIDEBOARD]", "[COMMANDER]", "[CRIATURAS]": a whole bracketed line.
const BRACKET_HEADER_RE = /^\[[^[\]]+\]$/

/**
 * The section a trimmed, non-blank, non-`#` line switches to when it is a
 * header, else null (a card line).
 *
 *  - ANY `//` line is a header, classified by SUBSTRING ("// Sideboard (10)",
 *    "// Commanders (2)", "// Maybeboard (5)"); an unrecognized one ("// Ramp")
 *    switches back to the deck, as the API does.
 *  - A bare header must match the API's list exactly, after a trailing/leading
 *    colon ("Sideboard:", "Tokens :").
 *  - A bracketed line is a header classified by substring, "comandante"
 *    included.
 */
export function classifyHeader(line: string): DeckSection | null {
  const lower = line.toLowerCase()
  if (lower.startsWith('//')) {
    if (lower.includes('sideboard')) return 'sideboard'
    if (lower.includes('maybe') || lower.includes('considering')) return 'maybeboard'
    if (lower.includes('commander')) return 'commander'
    if (lower.includes('companion')) return 'companion'
    if (lower.includes('token') || lower.includes('sticker') || lower.includes('attraction')) return 'tokens'
    return 'main'
  }
  const h = lower.trim().replace(/^:+|:+$/g, '').trim()
  if (BARE_HEADERS.has(h)) {
    if (h.includes('sideboard')) return 'sideboard'
    if (h.includes('maybeboard') || h === 'maybe' || h === 'considering') return 'maybeboard'
    if (h.includes('commander')) return 'commander'
    if (h.includes('companion')) return 'companion'
    if (h.includes('token') || h === 'stickers' || h === 'attractions') return 'tokens'
    return 'main'
  }
  if (BRACKET_HEADER_RE.test(line.trim())) {
    if (lower.includes('sideboard')) return 'sideboard'
    if (lower.includes('maybe') || lower.includes('considering')) return 'maybeboard'
    if (lower.includes('commander') || lower.includes('comandante')) return 'commander'
    if (lower.includes('companion')) return 'companion'
    if (lower.includes('token') || lower.includes('sticker') || lower.includes('attraction')) return 'tokens'
    return 'main'
  }
  return null
}

// A single line that is NOT a deck card wherever it sits, without switching
// sections:
//  - TappedOut's "SB: 1 Pyroblast" prefix. parse_decklist_detailed has no SB:
//    handling at all (it reads the whole line as an unresolvable card name);
//    the older parse_decklist treats it as sideboard. Either way it is not a
//    card the editor can meaningfully edit, so it is carried through verbatim
//    and the analysis sees exactly the line it saw before.
//  - An Archidekt "{noDeck}" category marker in any TRAILING tag
//    ("1 Sol Ring [Ramp{noDeck}]", "[Maybeboard{noDeck}]", or an "^...^"
//    label), which the API files under maybeboard and drops (decks.py
//    _pop_trailing_tags + _tag_section). The web parse strips "[...]" from the
//    name, so the card used to become an editable deck row and an edit
//    rewrote it tagless, moving it INTO the deck. Other markers ("{noPrice}",
//    "{top}") do not take a card out of the deck in the API, so they do not
//    here either.
const SB_PREFIX_RE = /^sb:/i
// The API's trailing tag shapes, popped repeatedly from the end of the line
// while a non-empty name remains before them.
const TRAILING_TAG_RE = /\s*\[[^[\]]*\]\s*$/
const TRAILING_LABEL_RE = /\s*\^[^^]*\^\s*$/
function trailingTags(line: string): string[] {
  const tags: string[] = []
  let rest = line
  for (;;) {
    const m = TRAILING_TAG_RE.exec(rest) ?? TRAILING_LABEL_RE.exec(rest)
    if (!m || !rest.slice(0, m.index).trim()) return tags
    tags.push(m[0].trim().replace(/^[[^]+|[\]^]+$/g, ''))
    rest = rest.slice(0, m.index)
  }
}
function hasNoDeckTag(line: string): boolean {
  return trailingTags(line).some(t => /\{nodeck\}/i.test(t))
}
// An Archidekt "Commander" category on a card ("Thrasios [Commander{top}]",
// "[Ramp,Commander]"): the API's _tag_section puts that card in the COMMAND
// ZONE, whatever header it sits under, and the tag beats the header (decks.py
// `tag_section or current_section`). A {noDeck} tag wins over it; that line is
// a non-deck line before this is asked.
function hasCommanderTag(line: string): boolean {
  return trailingTags(line).some(t =>
    t.split(',').some(part => {
      const name = part.split('{')[0].trim().toLowerCase()
      return name === 'commander' || name === 'commanders'
    }))
}
function isNonDeckLine(line: string): boolean {
  return SB_PREFIX_RE.test(line) || hasNoDeckTag(line)
}

export type ScannedLine =
  | { raw: string; line: string; kind: 'blank' | 'comment' | 'loose' }
  | { raw: string; line: string; kind: 'header'; section: DeckSection }
  | { raw: string; line: string; kind: 'card'; section: DeckSection }

// The one line walker. `section` on a card line is the section in force for it.
// A blank line ends a commander / companion section, as in the API. Exported
// for lib/deckDocument.ts, which splits the maybeboard out of the non-deck
// content with this same walker.
export function scanDecklist(text: string): ScannedLine[] {
  const out: ScannedLine[] = []
  let section: DeckSection = 'main'
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) {
      if (section === 'commander' || section === 'companion') section = 'main'
      out.push({ raw, line, kind: 'blank' })
      continue
    }
    if (line.startsWith('#')) { out.push({ raw, line, kind: 'comment' }); continue }
    const header = classifyHeader(line)
    if (header !== null) {
      section = header
      out.push({ raw, line, kind: 'header', section })
      continue
    }
    if (isNonDeckLine(line)) { out.push({ raw, line, kind: 'loose' }); continue }
    // The card's own commander tag overrides the section in force (it does not
    // switch it for the lines after).
    out.push({ raw, line, kind: 'card', section: hasCommanderTag(line) ? 'commander' : section })
  }
  return out
}

// A card line that is a deck card (not under a non-deck header).
function isDeckCard(l: ScannedLine): l is Extract<ScannedLine, { kind: 'card' }> {
  return l.kind === 'card' && !NON_DECK.has(l.section)
}
/* eslint-enable i18next/no-literal-string */

// Strip trailing markers/metadata repeatedly until stable, so combined
// suffixes in any order (e.g. "Sol Ring (C21) 123 *F*") fully normalize.
export function stripNameMarkers(rest: string): string {
  let name = rest.trim()
  let prev = ''
  while (name !== prev) {
    prev = name
    name = name
      .replace(/\s*\*[^*]+\*\s*$/, '')                         // "*F*" / "*CMDR*"
      .replace(/\s+\[[^\]]+\]\s*$/, '')                        // "[C21]"
      .replace(/\s*\([A-Za-z0-9]{2,6}\)\s*[^\s,()[\]*]*$/, '')  // "(C21) 123", "(SLD) 123★"
      .trim()
  }
  return name
}

// Command-zone names carried by the decklist TEXT itself: deck cards under a
// commander header ("// Commander", "// Commanders (2)", "Commander",
// "[COMMANDER]"), or deck lines tagged *CMDR*. applySwaps uses this to carry the
// commander through an apply. The section ends at a blank line or any other
// header, as in the API. A card under a sideboard / maybeboard header is never
// a commander, *CMDR* or not: it stays in the carried-through section instead,
// so it can never be emitted in the command zone AND kept in the appended block.
export function extractCommanders(text: string): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  const push = (rest: string) => {
    const name = stripNameMarkers(rest.replace(/^(\d+)\s*[xX]?\s+/, ''))
    const key = name.toLowerCase()
    if (name && !seen.has(key)) {
      seen.add(key)
      out.push(name)
    }
  }
  for (const l of scanDecklist(text)) {
    if (!isDeckCard(l)) continue
    if (/\*cmdr\*/i.test(l.line) || l.section === 'commander') push(l.line)
  }
  return out
}

/**
 * Companion names the decklist TEXT carries: deck cards under a companion
 * header ("// Companion", "Companion", "[COMPANION]"). They are deck cards to
 * the API (main + commander + companion), so they ride every edit as their
 * own section and are never re-serialized into the main deck.
 */
export function extractCompanions(text: string): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const l of scanDecklist(text)) {
    if (!isDeckCard(l) || l.section !== 'companion' || /\*cmdr\*/i.test(l.line)) continue
    const name = stripNameMarkers(l.line.replace(/^(\d+)\s*[xX]?\s+/, ''))
    const key = frontKey(name)
    if (name && !seen.has(key)) {
      seen.add(key)
      out.push(name)
    }
  }
  return out
}

/**
 * The full command zone for an edit: the row's own commander / partner fields,
 * then every commander the decklist TEXT names (sections, *CMDR*, Archidekt
 * "[Commander]" tags), de-duplicated by DFC front face. A legacy row can have
 * an empty partner field while its text tags two commanders; using the fields
 * alone left the second one as an editable, cuttable body row, and a save then
 * wrote it into the main deck untagged.
 */
export function commandZone(
  decklist: string | null | undefined,
  ...names: (string | null | undefined)[]
): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of [...names, ...extractCommanders(decklist ?? '')]) {
    const name = (raw ?? '').trim()
    const key = name ? frontKey(name) : ''
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(name)
  }
  return out
}

export function parseDecklist(text: string): EditableCard[] {
  const out: EditableCard[] = []
  const seen = new Map<string, number>() // name_lower -> index in out
  for (const l of scanDecklist(text)) {
    if (!isDeckCard(l)) continue
    const line = l.line

    let qty = 1
    let rest = line
    const m = line.match(/^(\d+)\s*[xX]?\s+(.+)$/)
    if (m) {
      qty = parseInt(m[1], 10) || 1
      rest = m[2]
    }

    const name = stripNameMarkers(rest)
    if (!name) continue

    const key = name.toLowerCase()
    const existing = seen.get(key)
    if (existing != null) {
      out[existing].quantity += qty
    } else {
      seen.set(key, out.length)
      out.push({ name, quantity: qty })
    }
  }
  return out
}

// The NON-DECK content of a decklist TEXT: every section under a sideboard /
// maybeboard / tokens header (from that header up to the next deck header or
// the end), plus the per-line non-deck forms ("SB: ..." and {noDeck}-tagged
// lines). These are exactly the lines parseDecklist leaves out, found by the
// same scanner, so nothing the editor shows as a deck row is ever duplicated
// here and nothing it hides is lost.
//
// Why it exists: every apply path round-trips the list through parseDecklist
// -> serializeDecklist, and the parse drops this content. The stored text is
// replaced whole on save, so a single edit used to delete the user's sideboard
// and maybeboard for good. Callers extract from the ORIGINAL text and hand the
// result to serializeDecklist, which re-appends it after the main list.
//
// Line CONTENT is preserved as written (comments and blank lines inside a
// section included; CRLF line endings normalize to LF, like every other
// serialize). Blank lines trailing a block are dropped, since they were
// separators; blocks are joined by one blank line. A second extract of the
// serialized result returns the same string, so repeated edits never grow or
// duplicate it. Returns '' for a deck with no such content, which
// serializeDecklist treats as "nothing to append", so those decks serialize
// exactly as before.
export function extractNonDeckSections(text: string): string {
  const blocks: string[][] = []
  // `region` is true while under a non-deck header; a block opened by a loose
  // line alone stays open only across further loose / blank lines.
  let current: string[] | null = null
  let region = false
  const close = () => {
    if (current) {
      while (current.length > 0 && current[current.length - 1].trim() === '') current.pop()
      if (current.length > 0) blocks.push(current)
    }
    current = null
    region = false
  }
  for (const l of scanDecklist(text)) {
    if (l.kind === 'header') {
      if (!NON_DECK.has(l.section)) { close(); continue }
      if (!current) current = []
      region = true
      current.push(l.raw)
      continue
    }
    if (region) { current!.push(l.raw); continue }
    if (l.kind === 'loose') {
      if (!current) current = []
      current.push(l.raw)
      continue
    }
    if (current && l.kind === 'blank') { current.push(l.raw); continue }
    // A deck card or comment ends a loose-only block.
    if (current) close()
  }
  close()
  return blocks.map(b => b.join('\n')).join('\n\n')
}

// `extraSections` is appended verbatim after the main list (see
// extractNonDeckSections). Omitted or blank, the output is unchanged from the
// two-argument form, byte for byte.
export function serializeDecklist(
  cards: EditableCard[],
  commander: string | readonly string[] | null,
  extraSections?: string | null,
  // Companions (extractCompanions): their own "// Companion" section after
  // the command zone, never main-deck rows. Omitted, the output is unchanged.
  companions?: readonly string[] | null,
): string {
  const commanders = (commander == null ? [] : Array.isArray(commander) ? commander : [commander]) as string[]
  const companionList = (companions ?? []).filter(c => !new Set(commanders.map(frontKey)).has(frontKey(c)))
  // Match by DFC front face so a full-name commander still suppresses a deck
  // row parsed under its front-face name (and vice versa).
  const commanderKeys = new Set([...commanders, ...companionList].map(frontKey))
  const lines: string[] = []
  if (commanders.length > 0) {
    // eslint-disable-next-line i18next/no-literal-string -- decklist syntax, error code or class name
    lines.push('// Commander')
    for (const c of commanders) lines.push(`1 ${c}`)
    lines.push('')
  }
  if (companionList.length > 0) {
    // eslint-disable-next-line i18next/no-literal-string -- decklist syntax
    lines.push('// Companion')
    for (const c of companionList) lines.push(`1 ${c}`)
    lines.push('')
  }
  for (const c of cards) {
    if (commanderKeys.has(frontKey(c.name))) continue
    lines.push(`${c.quantity} ${c.name}`)
  }
  const main = lines.join('\n')
  const extra = extraSections ?? ''
  if (!extra.trim()) return main
  // A blank line before the first extra header, so a commander-section scope
  // or the last deck row can never run into it.
  // eslint-disable-next-line i18next/no-literal-string -- decklist syntax, error code or class name
  return main ? `${main}\n\n${extra}` : extra
}

// A cut/add pair from /decks/adjust-bracket. Kept structural (not imported from
// api/hooks) so this pure helper has no dependency on the API layer.
export interface ApplySwapPair {
  // Optional/nullable: a malformed pair with no cut is skipped, never thrown on.
  cut?: { name: string } | null
  add: { name: string } | null
  group_id: string | null
}

// Front face of a DFC/split/adventure name ("Fire // Ice" -> "Fire"), lowercased
// for match keys. The API canonicalizes cuts the same way, so a cut named by its
// full DFC name still removes the deck row parsed under its front-face name.
// Exported so the adjust UI can count copies with the SAME key the apply uses.
export function frontKey(name: string): string {
  return name.split('//')[0].trim().toLowerCase()
}

// Drop every pasted row naming one of the commanders and prepend a
// `// Commander` section carrying them, so the API never counts a commander
// twice. Matched on ANY face (sameCard) plus the front key, which also splits
// an unspaced "Front//Back": a picker holding the full DFC name "Ashling,
// Rekindled // Ashling, Rimebound" must still strip a paste row reading just
// "1 Ashling, Rekindled" (that miss sent 101 cards). Same rule as iOS.
export function withCommanderSection(decklist: string, commanders: string[]): string {
  // A blank commander must never match ("" would strip every blank line and
  // `//` header) and must never be serialized as an empty "1 " row.
  const named = commanders.map(c => c.trim()).filter(Boolean)
  if (named.length === 0) return decklist
  const commanderKeys = new Set(named.map(frontKey))
  const isCommander = (name: string) =>
    !!name.trim() && (commanderKeys.has(frontKey(name)) || named.some(c => sameCard(name, c)))
  // Only MAIN-DECK card rows are candidates, classified by the same walker as
  // parseDecklist: a sideboard / maybeboard / tokens row, an "SB:" line or a
  // {noDeck}-tagged line is not in the deck and rides through verbatim.
  // scanDecklist splits on \r?\n, so its lines index 1:1 against a plain
  // '\n' split, which keeps each kept line byte-for-byte (CR included).
  const scanned = scanDecklist(decklist)
  const filtered = decklist.split('\n').filter((_raw, i) => {
    const l = scanned[i]
    if (!l || !isDeckCard(l)) return true
    const rest = l.line.replace(/^\d+\s*[xX]?\s+/, '')
    const name = stripNameMarkers(rest)
    // Also try the old loose cut at the first "(" for set/collector suffixes
    // stripNameMarkers does not recognize.
    return !(isCommander(name) || isCommander(stripNameMarkers(rest.replace(/\s*\(.*$/, ''))))
  })
  const commanderSection = named.map(c => `1 ${c}`).join('\n')
  // eslint-disable-next-line i18next/no-literal-string -- decklist syntax
  return `// Commander\n${commanderSection}\n\n${filtered.join('\n')}`
}

// Per-swap cut overrides (swap index -> alternate cut name) chosen in the UI.
// A Record is accepted so callers holding plain objects need no conversion.
export type CutOverrides = Map<number, string> | Record<number, string>

function overrideAt(overrides: CutOverrides | undefined, i: number): string | undefined {
  if (!overrides) return undefined
  const v = overrides instanceof Map ? overrides.get(i) : overrides[i]
  return typeof v === 'string' && v.trim() ? v : undefined
}

// Thrown by applySwaps when the accepted cuts (originals + overrides) ask for
// more copies of a card than the deck actually runs. Nothing is applied: the
// caller shows the message instead of producing a corrupt decklist.
export class SwapConflictError extends Error {
  /** Stable code for the render site; `message` stays English (logs). */
  // eslint-disable-next-line i18next/no-literal-string -- decklist syntax, error code or class name
  readonly code = 'swap_conflict'
  // Display names of the over-requested cards, in first-seen order.
  readonly conflicts: string[]
  constructor(conflicts: string[]) {
    super(
      conflicts.length === 1
        // eslint-disable-next-line i18next/no-literal-string -- the English message (logs); screens render swapConflictMessage()
        ? `Not enough copies of ${conflicts[0]} to cut. Change or skip a swap.`
        // eslint-disable-next-line i18next/no-literal-string -- as above
        : `Not enough copies of ${conflicts.join(', ')} to cut. Change or skip a swap.`,
    )
    // eslint-disable-next-line i18next/no-literal-string -- decklist syntax, error code or class name
    this.name = 'SwapConflictError'
    this.conflicts = conflicts
  }
}

/**
 * The conflict sentence a screen shows, in the active language
 * (common:swap_conflict). Its English equals the error's `message`. The card
 * names are data and stay as they are.
 */
export function swapConflictMessage(err: SwapConflictError): string {
  return tc('swap_conflict', { names: err.conflicts.join(', ') })
}

// All indices that are effectively accepted, with combo groups expanded: a
// non-null group_id is all-or-nothing, so accepting any member accepts them all.
// Exported so the UI counts the same effective cuts the apply will make.
export function expandAccepted(swaps: ApplySwapPair[], acceptedIdx: Set<number>): Set<number> {
  const acceptedGroups = new Set<string>()
  swaps.forEach((s, i) => {
    if (s.group_id && acceptedIdx.has(i)) acceptedGroups.add(s.group_id)
  })
  const out = new Set<number>()
  swaps.forEach((s, i) => {
    if (s.group_id ? acceptedGroups.has(s.group_id) : acceptedIdx.has(i)) out.add(i)
  })
  return out
}

// Optional apply behaviour. Both fields are OFF/absent by default, so the
// adjust-bracket path is byte-for-byte what it always was.
export interface ApplyOptions {
  // MANA-TUNE MODE. A manabase answer routinely adds a basic the deck already
  // runs. Singleton dedupe applied the cut and dropped the add, so the deck
  // SHRANK by one card per such swap. With this set, an add whose front face
  // is already present INCREMENTS that row by one, once per successfully
  // applied swap. Adjust-bracket leaves it off and keeps singleton dedupe,
  // which is correct there: it never proposes a second copy of a singleton.
  quantityAdds?: boolean
  // FALLBACK COMMAND ZONE, used only when the decklist text carries no
  // commander marker. An untagged legacy deck whose commander was selected
  // separately had nothing protecting it from a cut.
  commanders?: readonly (string | null | undefined)[]
}

// The command zone this apply must protect: the text markers when the deck has
// them, else the caller's fallback. Text wins, because a deck that names its
// own commander is the authority on its own commander. Mirrors the iOS
// DeckEditState.effectiveCommanders so the two platforms cannot drift.
function effectiveCommanders(
  decklist: string,
  fallback?: readonly (string | null | undefined)[],
): string[] {
  const fromText = extractCommanders(decklist)
  if (fromText.length > 0) return fromText
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of fallback ?? []) {
    const name = (raw ?? '').trim()
    if (!name) continue
    const key = frontKey(name)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(name)
  }
  return out
}

// Apply the accepted subset of a swap list to a decklist TEXT, returning new
// decklist text.
//
// Semantics:
//  - EFFECTIVE CUT: cutOverrides[i] (an alternate cut the user picked) wins over
//    the swap's own cut name.
//  - Cuts remove one copy: quantity>1 rows decrement (not delete), quantity 1
//    rows are removed. A cut name is matched by its DFC front face so "Fire //
//    Ice" cuts the "Fire" row.
//  - QUANTITY PRE-VALIDATION: before anything mutates, the accepted cuts are
//    tallied per front-face key. Asking for more copies of a card than the deck
//    runs throws SwapConflictError and applies NOTHING (a partial apply here
//    silently corrupted the list). A deck legally running many copies may be cut
//    once per spare copy.
//  - An unresolvable cut (no name, the commander, or a card not in the deck) is
//    skipped, AND its add is dropped with it: an add is only emitted when its
//    cut actually happened, so the deck never inflates past 100.
//  - Adds append a quantity-1 row (skipping any add already present, so we never
//    duplicate a card the deck already runs) UNLESS options.quantityAdds is set.
//  - GROUP ATOMICITY: swaps that share a non-null group_id are all-or-nothing.
//    If ANY index in a group is in acceptedIdx, the WHOLE group is treated as
//    accepted (a combo line is completed or not - never half-applied). If ANY
//    member of an accepted group is unresolvable, the entire group is dropped.
export function applySwaps(
  decklist: string,
  swaps: ApplySwapPair[],
  acceptedIdx: Set<number>,
  cutOverrides?: CutOverrides,
  options?: ApplyOptions,
): string {
  const accepted = expandAccepted(swaps, acceptedIdx)

  const cards = parseDecklist(decklist)
  // Command zone: the text markers when the deck has them, else the caller's
  // own commander/partner. Preserved through the apply (the output re-emits
  // the "// Commander" section) and never a valid cut target. Losing this
  // marker made the re-analyzed deck commanderless and illegal, and an
  // UNTAGGED legacy deck had no marker to lose - its separately selected
  // commander was just another cuttable row.
  const commanders = effectiveCommanders(decklist, options?.commanders)
  // ONE locked zone for availability, cut validation and removal: the
  // command zone AND the companions. A cut naming either is rejected.
  const companions = extractCompanions(decklist)
  const commanderKeys = new Set([...commanders, ...companions].map(frontKey))
  // Rows + available copies per DFC front-face key (commander rows excluded, so
  // they can never be spent by a cut).
  const rowsByKey = new Map<string, number[]>()
  const availableByKey = new Map<string, number>()
  cards.forEach((c, i) => {
    const key = frontKey(c.name)
    if (commanderKeys.has(key)) return
    const rows = rowsByKey.get(key)
    if (rows) rows.push(i)
    else rowsByKey.set(key, [i])
    availableByKey.set(key, (availableByKey.get(key) ?? 0) + c.quantity)
  })

  const effectiveCut = (i: number): string | undefined => overrideAt(cutOverrides, i) ?? swaps[i].cut?.name ?? undefined

  // Pass 1: which accepted swaps have a cut we can actually make?
  const unresolvable = new Set<number>()
  accepted.forEach(i => {
    const name = effectiveCut(i)
    if (!name) { unresolvable.add(i); return }
    const key = frontKey(name)
    if (commanderKeys.has(key)) { unresolvable.add(i); return }
    if ((availableByKey.get(key) ?? 0) === 0) unresolvable.add(i)
  })
  // A combo line is rejected whole when any member cannot be cut.
  const rejectedGroups = new Set<string>()
  unresolvable.forEach(i => {
    const gid = swaps[i].group_id
    if (gid) rejectedGroups.add(gid)
  })
  const applying = [...accepted]
    .sort((a, b) => a - b)
    .filter(i => {
      if (unresolvable.has(i)) return false
      const gid = swaps[i].group_id
      return !(gid && rejectedGroups.has(gid))
    })

  // Pass 2: quantity check across everything we are about to apply.
  const wanted = new Map<string, number>()
  const displayName = new Map<string, string>()
  applying.forEach(i => {
    const name = effectiveCut(i) as string
    const key = frontKey(name)
    wanted.set(key, (wanted.get(key) ?? 0) + 1)
    if (!displayName.has(key)) displayName.set(key, name)
  })
  const conflicts: string[] = []
  wanted.forEach((count, key) => {
    if (count > (availableByKey.get(key) ?? 0)) conflicts.push(displayName.get(key) ?? key)
  })
  if (conflicts.length > 0) throw new SwapConflictError(conflicts)

  // Pass 3: mutate. Every cut here is guaranteed to have a copy waiting.
  const removed = new Set<number>() // row indices fully removed
  applying.forEach(i => {
    const key = frontKey(effectiveCut(i) as string)
    const rows = rowsByKey.get(key) ?? []
    for (const at of rows) {
      if (removed.has(at)) continue
      if (cards[at].quantity > 1) cards[at].quantity -= 1
      else removed.add(at)
      break
    }
  })

  const out: EditableCard[] = cards.filter((_, i) => !removed.has(i))
  // Present names (after cuts) guard against duplicate adds.
  const present = new Set(out.map(c => frontKey(c.name)))
  const rowIndexByKey = new Map<string, number>()
  if (options?.quantityAdds) {
    out.forEach((c, i) => {
      const key = frontKey(c.name)
      if (!rowIndexByKey.has(key)) rowIndexByKey.set(key, i)
    })
  }

  applying.forEach(i => {
    const add = swaps[i].add
    if (!add?.name) return
    const key = frontKey(add.name)
    if (present.has(key)) {
      if (!options?.quantityAdds) return
      const at = rowIndexByKey.get(key)
      if (at === undefined) return
      out[at].quantity += 1
      return
    }
    present.add(key)
    out.push({ name: add.name, quantity: 1 })
    if (options?.quantityAdds) rowIndexByKey.set(key, out.length - 1)
  })

  // The sideboard / maybeboard ride along untouched: a swap is an edit to
  // the deck, never to the cards the user parked beside it.
  return serializeDecklist(out, commanders.length > 0 ? commanders : null, extractNonDeckSections(decklist), companions)
}

// Stable equality for the "changes pending" badge: same names + quantities,
// order-independent.
export function decklistsEqual(a: EditableCard[], b: EditableCard[]): boolean {
  if (a.length !== b.length) return false
  const norm = (cards: EditableCard[]) =>
    new Map(cards.map(c => [c.name.toLowerCase(), c.quantity]))
  const ma = norm(a)
  const mb = norm(b)
  if (ma.size !== mb.size) return false
  for (const [k, v] of ma) {
    if (mb.get(k) !== v) return false
  }
  return true
}

// Total cards in a decklist TEXT, using the same parser (and therefore the same
// section/marker handling) every other helper here uses. The rail's "Cut to
// 100" affordance needs a count when the analysis has not supplied one.
export function countDecklistCards(decklist: string): number {
  return parseDecklist(decklist).reduce((n, c) => n + c.quantity, 0)
}

// Remove ONE copy per occurrence of each name in `names`, preserving the rest
// of the decklist TEXT verbatim.
//
// Semantics (deliberately different from applySwaps, which round-trips through
// parse -> serialize and therefore rewrites the whole list):
//  - The text is edited LINE BY LINE. Section headers, comments, blank lines,
//    ordering, set codes and *F* markers all survive untouched, because a trim
//    is an edit to the user's own list, not a regeneration of it.
//  - Passing the same name twice decrements that card's quantity by 2.
//  - A quantity that reaches 0 removes the line entirely; a quantity above 1 is
//    rewritten in place ("3 Forest" -> "2 Forest").
//  - Names match by DFC front face (frontKey) after the same marker stripping
//    parseDecklist does, so "Fire // Ice" removes the row parsed as "Fire".
//  - Lines in a suppressed section (sideboard, maybeboard, tokens) and the
//    command zone are never touched: the same cards a cut may never spend in
//    applySwaps. An UNTAGGED deck falls back to options.commanders, so a
//    separately selected commander is protected too - the mana panel's
//    cuts-only path relies on that, and a pasted list often has no
//    "// Commander" header at all.
//  - A name the deck does not run (or runs fewer copies of than requested) is
//    simply not removed. Nothing throws: the caller is applying a server-picked
//    cut list against a list the user may have edited since.
export function removeCards(
  decklist: string,
  names: readonly string[],
  options?: Pick<ApplyOptions, 'commanders'>,
): string {
  const wanted = new Map<string, number>()
  for (const raw of names) {
    const key = frontKey(stripNameMarkers(raw ?? ''))
    if (!key) continue
    wanted.set(key, (wanted.get(key) ?? 0) + 1)
  }
  if (wanted.size === 0) return decklist

  // Locked: the command zone AND the companions (same set as applySwaps).
  const commanderKeys = new Set([...effectiveCommanders(decklist, options?.commanders), ...extractCompanions(decklist)].map(frontKey))
  const out: string[] = []

  // The shared scanner: headers, comments, blanks, non-deck sections and the
  // per-line non-deck forms all pass through untouched.
  for (const l of scanDecklist(decklist)) {
    const { raw, line } = l
    if (!isDeckCard(l)) { out.push(raw); continue }

    // Quantity prefix on the RAW line so leading whitespace and the separator
    // ("2x " vs "2 ") are preserved exactly when the row is rewritten.
    const m = raw.match(/^(\s*)(\d+)(\s*[xX]?\s+)(.+)$/)
    const name = stripNameMarkers(m ? m[4] : line)
    if (!name) { out.push(raw); continue }
    const key = frontKey(name)
    if (commanderKeys.has(key)) { out.push(raw); continue }

    let take = wanted.get(key) ?? 0
    if (take <= 0) { out.push(raw); continue }
    let qty = m ? (parseInt(m[2], 10) || 1) : 1
    while (take > 0 && qty > 0) { take -= 1; qty -= 1 }
    wanted.set(key, take)
    if (qty <= 0) continue
    out.push(m ? `${m[1]}${qty}${m[3]}${m[4]}` : raw)
  }

  return out.join('\n')
}

// --- Card search: add one card to a decklist ---------------------------------

/**
 * True when `name` is already in the deck, command zone included.
 *
 * Matched by DFC front face, the same key `applySwaps` uses, so "Fire // Ice"
 * and "Fire" are one card and not two.
 */
export function decklistHasCard(decklist: string, name: string): boolean {
  const key = frontKey(name)
  if (!key) return false
  if (extractCommanders(decklist).some(c => frontKey(c) === key)) return true
  return parseDecklist(decklist).some(c => frontKey(c.name) === key)
}

/**
 * One copy of `name` appended to `decklist`, returned as normalized text.
 *
 * Duplicate adds are SKIPPED rather than stacked, mirroring `applySwaps`: this
 * is a singleton format, and the caller (the card-search panel) already
 * disables its add control for a card the deck runs. Returning the round-tripped
 * list on that path keeps the function total - a caller never has to branch on
 * "did it actually add", it compares the result.
 *
 * The command zone is preserved through the round trip via `serializeDecklist`,
 * which re-emits the "// Commander" section, and so are the non-deck sections
 * (see `extractNonDeckSections`). Losing that marker is what once
 * made a re-analyzed deck commanderless and illegal.
 */
export function addCard(decklist: string, name: string): string {
  const clean = name.trim()
  const cards = parseDecklist(decklist)
  const commanders = extractCommanders(decklist)
  const commanderArg = commanders.length > 0 ? commanders : null
  // Non-deck sections (sideboard, maybeboard, tokens) are carried through.
  const extra = extractNonDeckSections(decklist)
  const companions = extractCompanions(decklist)
  if (!clean) return serializeDecklist(cards, commanderArg, extra, companions)
  if (!decklistHasCard(decklist, clean)) {
    cards.push({ name: clean, quantity: 1 })
  }
  return serializeDecklist(cards, commanderArg, extra, companions)
}

// --- Analysis staleness ------------------------------------------------------

// cyrb53: a fast 53-bit string hash. Not cryptographic; it only has to tell
// two card multisets apart.
function cyrb53(str: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)
}

/**
 * A hash of exactly what an analysis is computed FROM: the main, commander and
 * companion cards of a decklist TEXT, as `section|name -> total quantity`.
 *
 * Built on the shared section scanner, so it agrees with parseDecklist about
 * which lines are deck cards. Sideboard, maybeboard, tokens, loose non-deck
 * lines ("SB:", {noDeck}) and comments never change it. Names go through the
 * same marker stripping as parseDecklist and then frontKey (DFC front face,
 * lowercased), BEFORE duplicates are folded, so "1 Delver of Secrets" and
 * "1 Delver of Secrets // Insectile Aberration" hash the same, and two
 * "1 Sol Ring" lines hash like one "2 Sol Ring". A *CMDR* line counts as a
 * commander wherever it sits. Moving a card between the command zone and the
 * main deck changes the hash.
 *
 * Deliberately NOT hashDecklist (lib/deckTitle.ts): that one is the linked
 * source's divergence contract and must not move.
 */
export function analysisContentHash(text: string | null | undefined): string {
  const totals = new Map<string, number>()
  for (const l of scanDecklist(text ?? '')) {
    if (!isDeckCard(l)) continue
    let qty = 1
    let rest = l.line
    const m = l.line.match(/^(\d+)\s*[xX]?\s+(.+)$/)
    if (m) {
      qty = parseInt(m[1], 10) || 1
      rest = m[2]
    }
    const name = stripNameMarkers(rest)
    if (!name) continue
    // eslint-disable-next-line i18next/no-literal-string -- a section code
    const section: DeckSection = /\*cmdr\*/i.test(l.line) ? 'commander' : l.section
    const key = `${section}|${frontKey(name)}`
    totals.set(key, (totals.get(key) ?? 0) + qty)
  }
  const entries = [...totals.entries()]
    .filter(([, q]) => q > 0)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, q]) => `${k}=${q}`)
  // eslint-disable-next-line i18next/no-literal-string -- a hash version prefix
  return `h1:${cyrb53(entries.join('\n'))}`
}

/**
 * True when the analysis carries a content hash and the deck's CURRENT text no
 * longer matches it: the list was edited after this rating.
 *
 * `resolvedText` must be the already-resolved list (DeckDetailV2's
 * getDecklist(), which synthesizes text for a legacy row), never the raw
 * stored column and never ''. An analysis with no hash (saved before the
 * stamp existed) is never called stale.
 */
export function isAnalysisStale(
  analysis: { analyzed_content_hash?: string } | null | undefined,
  resolvedText: string | null | undefined,
): boolean {
  const stamped = analysis?.analyzed_content_hash
  if (!stamped || resolvedText == null) return false
  return stamped !== analysisContentHash(resolvedText)
}
